<script setup>
import {computed, nextTick, ref, watch} from "vue";
import Select2 from "vue3-select2-component";

import useHelpers from "@/Composables/UseHelpers";

const {$t} = useHelpers();

const emit = defineEmits([
  'update:participant_address1',
  'update:participant_address2',
  'update:participant_postcode',
  'update:participant_city',
  'update:participant_state',
  'update:participant_country',
  'select',
]);

const participant_state_model = ref('')
const participant_city_model = ref('')
const participant_country_model = ref('')
const participant_postcode_model = ref('')

const props = defineProps({
  participant_address_field_enabled: {
    required: true,
  },
  participant_email_required: {
    required: true,
  },
  participant_address_field_required: {
    required: true,
  },
  participant_address1: {
    required: true,
  },
  participant_address2: {
    required: true,
  },
  participant_postcode: {
    required: true,
  },
  participant_city: {
    required: true,
  },
  participant_state: {
    required: true,
  },
  participant_country: {
    required: true,
  },
  mandate_enabled: {
    required: true,
  },
  mandate_application_type: {
    required: false,
    default: null
  },
  city_opts: {
    required: true,
  },
  state_opts: {
    required: true,
  },
  country_opts: {
    required: true,
  },
  default_country: {
    required: true,
  },
  errors: {
    required: true,
  }
});

const select2Config = ref({
  width: '100%',
  default: props.default_country
});

watch(() => participant_postcode_model.value, (val) => {
  participant_postcode_model.value = val.replace(/\D/g, '')
});

const filteredParticipantCityOpts = computed(() => {
  return props.city_opts[participant_state_model.value] ?? []
});

function fillRandomAddress() {
  const addresses = [
    { address1: 'No. 12, Jalan Mawar 3', address2: 'Taman Bunga Raya', postcode: '40000', state: 'Selangor', city: 'Shah Alam', country: 'Malaysia' },
    { address1: 'Lot 5, Jalan Kenanga', address2: 'Kampung Baru', postcode: '50300', state: 'W.P. Kuala Lumpur', city: 'Kuala Lumpur', country: 'Malaysia' },
    { address1: 'No. 88, Lorong Cempaka 7', address2: 'Taman Sri Indah', postcode: '81100', state: 'Johor', city: 'Johor Bahru', country: 'Malaysia' },
    { address1: 'No. 3, Jalan Delima 2', address2: 'Taman Harmoni', postcode: '11700', state: 'Pulau Pinang', city: 'Gelugor', country: 'Malaysia' },
    { address1: 'Blok A, Tingkat 3', address2: 'Flat Sri Perak', postcode: '88000', state: 'Sabah', city: 'Kota Kinabalu', country: 'Malaysia' },
  ];
  const addr = addresses[Math.floor(Math.random() * addresses.length)];

  emit('update:participant_address1', addr.address1);
  emit('update:participant_address2', addr.address2);
  participant_postcode_model.value = addr.postcode;
  emit('update:participant_postcode', addr.postcode);

  participant_state_model.value = addr.state;
  emit('update:participant_state', addr.state);

  nextTick(() => {
    participant_city_model.value = addr.city;
    emit('update:participant_city', addr.city);
  });

  participant_country_model.value = addr.country;
  emit('update:participant_country', addr.country);
}

defineExpose({ fillRandomAddress });
</script>

<template>
  <template
      v-if="participant_address_field_enabled || (participant_address_field_enabled && mandate_application_type === '01')">
    <!-- Address Line 1 -->
    <div class="form-group row mb-2">
      <label class="col-4 col-form-label" for="participant_address1">
        {{ $t('donation-form.address') }}

        <!-- Required -->
        <template v-if="participant_address_field_required">
          <span class="text-red"> *</span>
        </template>
      </label>
      <div class="col-8">
        <input
            type="text"
            id="participant_address1"
            name="participant_address1"
            :class="{ 'form-control': true, 'is-invalid': errors.participant_address1 }"
            :placeholder="$t('donation-form.address_line1')"
            required="required"
            :value="participant_address1"
            @input="emit('update:participant_address1', $event.target.value)"
        >

        <!-- Error message -->
        <div class="invalid-feedback" v-if="errors.participant_address1">
          {{ errors.participant_address1 }}
        </div>
      </div>
    </div>

    <!-- Address Line 2 -->
    <div class="form-group row mb-2">
      <label class="col-4 col-form-label" for="participant_address2"></label>
      <div class="col-8">
        <input
            type="text"
            id="participant_address2"
            name="participant_address2"
            class="form-control"
            :placeholder="$t('donation-form.address_line2')"
            required="required"
            :value="participant_address2"
            @input="emit('update:participant_address2', $event.target.value)"
        >
      </div>
    </div>

    <!-- Postcode -->
    <div class="form-group row mb-2">
      <label class="col-4 col-form-label" for="participant_postcode">
        {{ $t('donation-form.postcode') }}

        <!-- Required -->
        <template v-if="participant_address_field_required">
          <span class="text-red"> *</span>
        </template>
      </label>
      <div class="col-8">
        <input
            type="text"
            id="participant_postcode"
            name="participant_postcode"
            inputmode="numeric"
            maxlength="5"
            :class="{ 'form-control': true, 'is-invalid': errors.participant_postcode }"
            required="required"
            :placeholder="$t('donation-form.postcode')"
            v-model="participant_postcode_model"
            @input="emit('update:participant_postcode', $event.target.value)"
        >

        <!-- Error message -->
        <div class="invalid-feedback" v-if="errors.participant_postcode">
          {{ errors.participant_postcode }}
        </div>
      </div>
    </div>

    <!-- State -->
    <div class="form-group row mb-2">
      <label class="col-4 col-form-label" for="participant_state">
        {{ $t('donation-form.state') }}

        <!-- Required -->
        <template v-if="participant_address_field_required">
          <span class="text-red"> *</span>
        </template>
      </label>
      <div class="col-8">
        <Select2
            :placeholder="$t('donation-form.select_state')"
            :options="Object.values(state_opts)"
            :class="{ 'is-invalid': Boolean(errors.participant_state) }"
            :settings="select2Config"
            v-model="participant_state_model"
            @select="emit('update:participant_state', participant_state_model)"
        />

        <!-- Error message -->
        <div class="invalid-feedback" v-if="errors.participant_state">
          {{ errors.participant_state }}
        </div>
      </div>
    </div>

    <!-- City -->
    <div class="form-group row mb-2">
      <label class="col-4 col-form-label" for="participant_city">
        {{ $t('donation-form.city') }}

        <!-- Required -->
        <template v-if="participant_address_field_required">
          <span class="text-red"> *</span>
        </template>
      </label>
      <div class="col-8">
        <Select2
            :placeholder="$t('donation-form.select_city')"
            :options="filteredParticipantCityOpts"
            :class="{ 'is-invalid': Boolean(errors.participant_city) }"
            :settings="select2Config"
            v-model="participant_city_model"
            @select="emit('update:participant_city', participant_city_model)"
        />

        <!-- Error message -->
        <div class="invalid-feedback" :class="{ 'd-block': errors.participant_city }" v-if="errors.participant_city">
          {{ errors.participant_city }}
        </div>
      </div>
    </div>

    <!-- Country -->
    <div class="form-group row mb-2">
      <label class="col-4 col-form-label" for="participant_country">
        {{ $t('donation-form.country') }}

        <!-- Required -->
        <template v-if="participant_address_field_required">
          <span class="text-red"> *</span>
        </template>
      </label>
      <div class="col-8">
        <Select2
            :placeholder="$t('donation-form.select_country')"
            :options="Object.values(country_opts)"
            :class="{ 'is-invalid': Boolean(errors.participant_country) }"
            :settings="select2Config"
            v-model="participant_country_model"
            @select="emit('update:participant_country', participant_country_model)"
        />

        <!-- Error message -->
        <div class="invalid-feedback" v-if="errors.participant_country">
          {{ errors.participant_country }}
        </div>
      </div>
    </div>
  </template>
</template>